<template>
  <div class="deliverability">
    <!-- ── Filters ─────────────────────────────────────────────── -->
    <div class="filterBar">
      <SearchSelect
        class="filterPicker"
        :modelValue="datePreset"
        @update:modelValue="datePreset = $event"
        :options="datePresetOptions"
        :hideSearch="true"
        :allowClear="false"
        placeholder="Date range"
      />

      <template v-if="datePreset === 'custom'">
        <input type="date" v-model="customStart" class="filterInput" />
        <span class="filterSep">&ndash;</span>
        <input type="date" v-model="customEnd" class="filterInput" />
      </template>

      <SearchSelect
        class="filterPicker"
        :modelValue="salesPlayId"
        @update:modelValue="salesPlayId = $event"
        :options="salesPlayOptions"
        :hideSearch="true"
        :allowClear="true"
        clearLabel="All sales plays"
        placeholder="All sales plays"
      />

      <SearchSelect
        class="filterPicker filterPickerWide"
        :modelValue="fromIdentity"
        @update:modelValue="fromIdentity = $event"
        :options="mailboxOptions"
        :hideSearch="true"
        :allowClear="true"
        clearLabel="All mailboxes"
        placeholder="All mailboxes"
      />
    </div>

    <!-- ── Loading / error ─────────────────────────────────────── -->
    <div v-if="loading" class="statusMsg">Loading report&hellip;</div>
    <div v-else-if="error" class="statusMsg error">{{ error }}</div>

    <template v-else-if="data">
      <!-- ── Summary stat cards ────────────────────────────────── -->
      <div class="summaryRow">
        <div class="statCard">
          <span class="statValue">{{ fmt(data.total_sent) }}</span>
          <span class="statLabel">Total emails sent</span>
        </div>
        <div class="statCard">
          <span class="statValue">{{ fmt(data.total_delivered) }}</span>
          <span class="statSub">{{ pct(data.delivery_rate) }} delivery rate</span>
          <span class="statLabel">Delivered</span>
        </div>
        <div class="statCard">
          <span class="statValue statValueDanger">{{ fmt(data.total_bounced) }}</span>
          <span class="statSub">{{ pct(data.bounce_rate) }} bounce rate</span>
          <span class="statLabel">Not delivered (bounced)</span>
        </div>
        <div class="statCard">
          <span class="statValue">{{ fmt(data.total_replied) }}</span>
          <span class="statSub">{{ pct(data.reply_rate) }} reply rate</span>
          <span class="statLabel">Replied</span>
          <div v-if="data.total_replied > 0" class="replyBreakdown">
            <span class="replyPos">{{ data.replied_positive }} positive</span>
            <span class="replySep">&middot;</span>
            <span class="replyNeg">{{ data.replied_negative }} negative</span>
            <template v-if="data.total_replied - data.replied_positive - data.replied_negative > 0">
              <span class="replySep">&middot;</span>
              <span class="replyPending">{{ data.total_replied - data.replied_positive - data.replied_negative }} pending</span>
            </template>
          </div>
        </div>
      </div>

      <!-- ── Charts (side by side) ─────────────────────────────── -->
      <div class="chartRow">
        <div class="chartSection">
          <h3 class="chartTitle">Sent vs. Delivered vs. Bounced vs. Replied</h3>
          <div class="chartWrap">
            <v-chart :option="volumeChartOption" autoresize style="height: 310px" />
          </div>
        </div>

        <div class="chartSection">
          <h3 class="chartTitle">Delivery, Bounce &amp; Reply Rate Over Time</h3>
          <div class="chartWrap">
            <v-chart :option="rateChartOption" autoresize style="height: 310px" />
          </div>
        </div>
      </div>

      <!-- ── Provenance charts (side by side) ─────────────────── -->
      <div
        v-if="providerChartOption || leadiqStatusChartOption"
        class="chartRow"
      >
        <div v-if="providerChartOption" class="chartSection">
          <h3 class="chartTitle">Bounces by Provider</h3>
          <div class="chartWrap">
            <v-chart :option="providerChartOption" autoresize style="height: 220px" />
          </div>
        </div>

        <div v-if="leadiqStatusChartOption" class="chartSection">
          <h3 class="chartTitle">Bounces by LeadIQ Verification</h3>
          <div class="chartWrap">
            <v-chart :option="leadiqStatusChartOption" autoresize style="height: 220px" />
          </div>
        </div>
      </div>

      <!-- ── Bounce detail table ───────────────────────────────── -->
      <div v-if="data.bounces.length > 0" class="tableSection">
        <h3 class="chartTitle">Bounce breakdown</h3>
        <div class="tableWrap">
          <table class="detailTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Email</th>
                <th>Mailbox</th>
                <th>Reachout</th>
                <th>Provenance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(b, i) in data.bounces" :key="i">
                <td>{{ b.date }}</td>
                <td>{{ b.email }}</td>
                <td>{{ b.from_identity || '—' }}</td>
                <td>
                  <router-link v-if="b.reachout_id" :to="`/actions/${b.reachout_id}`" class="reachoutPill">
                    <AppIcon name="email" :size="12" />
                    <span class="reachoutPillLabel">{{ b.reachout_subject || 'View reachout' }}</span>
                  </router-link>
                  <span v-else class="textMuted">—</span>
                </td>
                <td><DataProvenance :provider="b.email_provider" :leadiq-status="b.leadiq_status" data-type="email" :data-value="b.email" detailed /><span v-if="!b.email_provider" class="textMuted">—</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Reply detail table ────────────────────────────────── -->
      <div v-if="data.replies.length > 0" class="tableSection">
        <h3 class="chartTitle">Replies</h3>
        <div class="tableWrap">
          <table class="detailTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Email</th>
                <th>Mailbox</th>
                <th>Outcome</th>
                <th>Reply</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in data.replies" :key="i">
                <td>{{ r.date }}</td>
                <td>{{ r.email }}</td>
                <td>{{ r.from_identity || '—' }}</td>
                <td>
                  <span :class="['outcomeBadge', `outcome-${r.outcome}`]">{{ r.outcome }}</span>
                </td>
                <td class="snippetCell">{{ r.reply_snippet }}</td>
                <td>
                  <router-link v-if="r.contact_id" :to="`/contacts/${r.contact_id}`" class="threadLink">
                    View thread
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Unsubscribe detail table ─────────────────────────────── -->
      <div v-if="data.unsubscribes.length > 0" class="tableSection">
        <h3 class="chartTitle">Unsubscribes</h3>
        <div class="tableWrap">
          <table class="detailTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Email</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(u, i) in data.unsubscribes" :key="i">
                <td>{{ u.date }}</td>
                <td>{{ u.email }}</td>
                <td>{{ u.source }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="data.daily_stats.length === 0 && data.bounces.length === 0 && data.replies.length === 0 && data.unsubscribes.length === 0" class="emptyMsg">
        No email activity found for the selected period.
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart, BarChart } from 'echarts/charts'
import {
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import AppIcon from '@/components/AppIcon.vue'
import DataProvenance from '@/components/DataProvenance.vue'
import SearchSelect from '@/components/SearchSelect.vue'
import { datePresetOptions, REPORTS_FILTER_KEY } from '../../composables/useReportsFilters'
import { useEmailDeliverability } from '../../composables/useEmailDeliverability'
import type { ProvenanceBreakdown } from '../../composables/useEmailDeliverability'

use([LineChart, BarChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer])

const COLORS = {
  sent: '#3b82f6',
  delivered: '#06b6d4',
  bounced: '#ef4444',
  repliedPositive: '#10b981',
  repliedNegative: '#f59e0b',
  unsubscribed: '#94a3b8',
}

// ── Shared filters (via provide/inject from ReportsPage) ────────────
const { datePreset, customStart, customEnd, dateRange, salesPlayId, salesPlayOptions } = inject(REPORTS_FILTER_KEY)!
const fromIdentity = ref('')

// ── Data ────────────────────────────────────────────────────────────
const { data, loading, error, fetch: fetchData } = useEmailDeliverability()

const mailboxOptions = computed(() =>
  (data.value?.mailboxes || []).map((m) => ({ value: m, label: m }))
)

function reload() {
  fetchData({
    startDate: dateRange.value.startDate,
    endDate: dateRange.value.endDate,
    salesPlayId: salesPlayId.value || null,
    fromIdentity: fromIdentity.value || null,
  })
}

watch([dateRange, salesPlayId, fromIdentity], reload)

onMounted(reload)

// ── Charts ──────────────────────────────────────────────────────────

/** Build a complete list of YYYY-MM-DD strings covering the selected date range,
 *  padded by one day on each side so line charts render cleanly at the edges
 *  and single-day ranges still produce a visible line. */
function buildDateAxis(): string[] {
  const { startDate, endDate } = dateRange.value
  const start = new Date(startDate)
  const end = new Date(endDate)
  const dates: string[] = []
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  cur.setDate(cur.getDate() - 1) // pad one day before
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  last.setDate(last.getDate() + 1) // pad one day after
  while (cur <= last) {
    dates.push(cur.toISOString().slice(0, 10))
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

/** Format YYYY-MM-DD to MM/DD for display. */
function fmtDate(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${m}/${d}`
}

/** Common legend config. */
const LEGEND = {
  bottom: 0,
  textStyle: { fontSize: 11 },
  icon: 'roundRect',
  itemWidth: 14,
  itemHeight: 3,
  itemGap: 12,
  padding: [4, 0, 0, 0],
} as const

/** Index daily_stats by date for O(1) lookup. */
function buildStatsMap() {
  const map = new Map<string, { sent: number; delivered: number; bounced: number; replied_positive: number; replied_negative: number; unsubscribed: number }>()
  for (const s of data.value?.daily_stats || []) {
    map.set(s.date, s)
  }
  return map
}

const volumeChartOption = computed(() => {
  const dates = buildDateAxis()
  const map = buildStatsMap()

  return {
    tooltip: { trigger: 'axis' },
    legend: LEGEND,
    grid: { left: 50, right: 20, top: 20, bottom: 70 },
    xAxis: {
      type: 'category',
      data: dates.map(fmtDate),
      axisLabel: { fontSize: 11, rotate: dates.length > 10 ? 45 : 0 },
    },
    yAxis: { type: 'value' },
    series: [
      {
        name: 'Sent',
        type: 'line',
        data: dates.map((d) => map.get(d)?.sent ?? 0),
        lineStyle: { color: COLORS.sent, width: 2, type: 'dotted' },
        itemStyle: { color: COLORS.sent },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Delivered',
        type: 'line',
        data: dates.map((d) => map.get(d)?.delivered ?? 0),
        lineStyle: { color: COLORS.delivered, width: 2 },
        itemStyle: { color: COLORS.delivered },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Bounced',
        type: 'line',
        data: dates.map((d) => map.get(d)?.bounced ?? 0),
        lineStyle: { color: COLORS.bounced, width: 2 },
        itemStyle: { color: COLORS.bounced },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Replied (positive)',
        type: 'line',
        data: dates.map((d) => map.get(d)?.replied_positive ?? 0),
        lineStyle: { color: COLORS.repliedPositive, width: 2 },
        itemStyle: { color: COLORS.repliedPositive },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Replied (negative)',
        type: 'line',
        data: dates.map((d) => map.get(d)?.replied_negative ?? 0),
        lineStyle: { color: COLORS.repliedNegative, width: 2 },
        itemStyle: { color: COLORS.repliedNegative },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Unsubscribed',
        type: 'line',
        data: dates.map((d) => map.get(d)?.unsubscribed ?? 0),
        lineStyle: { color: COLORS.unsubscribed, width: 2 },
        itemStyle: { color: COLORS.unsubscribed },
        showSymbol: false,
        smooth: true,
      },
    ],
  }
})

const rateChartOption = computed(() => {
  const dates = buildDateAxis()
  const map = buildStatsMap()

  function rate(d: string, field: 'delivered' | 'bounced' | 'replied_positive' | 'replied_negative' | 'unsubscribed'): number {
    const s = map.get(d)
    if (!s || s.sent === 0) return 0
    return Math.round(((s[field] / s.sent) * 100) * 10) / 10
  }

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any[]) =>
        params.map((p: any) => `${p.marker} ${p.seriesName}: ${p.value}%`).join('<br/>'),
    },
    legend: LEGEND,
    grid: { left: 50, right: 20, top: 20, bottom: 70 },
    xAxis: {
      type: 'category',
      data: dates.map(fmtDate),
      axisLabel: { fontSize: 11, rotate: dates.length > 10 ? 45 : 0 },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { formatter: '{value}%' },
    },
    series: [
      {
        name: 'Delivery Rate',
        type: 'line',
        data: dates.map((d) => rate(d, 'delivered')),
        lineStyle: { color: COLORS.delivered, width: 2 },
        itemStyle: { color: COLORS.delivered },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Bounce Rate',
        type: 'line',
        data: dates.map((d) => rate(d, 'bounced')),
        lineStyle: { color: COLORS.bounced, width: 2 },
        itemStyle: { color: COLORS.bounced },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Positive Reply Rate',
        type: 'line',
        data: dates.map((d) => rate(d, 'replied_positive')),
        lineStyle: { color: COLORS.repliedPositive, width: 2 },
        itemStyle: { color: COLORS.repliedPositive },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Negative Reply Rate',
        type: 'line',
        data: dates.map((d) => rate(d, 'replied_negative')),
        lineStyle: { color: COLORS.repliedNegative, width: 2 },
        itemStyle: { color: COLORS.repliedNegative },
        showSymbol: false,
        smooth: true,
      },
      {
        name: 'Unsubscribe Rate',
        type: 'line',
        data: dates.map((d) => rate(d, 'unsubscribed')),
        lineStyle: { color: COLORS.unsubscribed, width: 2 },
        itemStyle: { color: COLORS.unsubscribed },
        showSymbol: false,
        smooth: true,
      },
    ],
  }
})

// ── Provenance charts ──────────────────────────────────────────────

const PROVIDER_COLORS: Record<string, string> = {
  salesforce: '#00a1e0',
  hubspot: '#ff7a59',
  pipedrive: '#1a1a1a',
  warmly: '#f59e0b',
  gong: '#7c3aed',
  leadiq: '#8b5cf6',
  exa: '#f59e0b',
  openai: '#10a37f',
  claude: '#d97706',
  unknown: '#d1d5db',
}

const LEADIQ_COLORS: Record<string, string> = {
  verified: '#10b981',
  verified_likely: '#34d399',
  unverified: '#f59e0b',
  invalid: '#ef4444',
  suppressed: '#94a3b8',
  unknown: '#d1d5db',
}

function provenanceBarOption(items: ProvenanceBreakdown[], colorMap: Record<string, string>) {
  if (!items.length) return null
  const labels = items.map(i => i.label)
  const counts = items.map(i => i.bounced)
  const colors = labels.map(l => colorMap[l] || '#94a3b8')
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any[]) => {
        const p = params[0]
        const item = items[p.dataIndex]
        return `${p.name}<br/>${p.marker} ${p.value} bounced (${(item.bounce_rate * 100).toFixed(2)}% of sent)`
      },
    },
    grid: { left: 80, right: 20, top: 10, bottom: 30 },
    xAxis: { type: 'value' },
    yAxis: {
      type: 'category',
      data: labels,
      axisLabel: { fontSize: 11 },
    },
    series: [{
      type: 'bar',
      data: counts.map((v, i) => ({ value: v, itemStyle: { color: colors[i] } })),
      barMaxWidth: 24,
    }],
  }
}

const providerChartOption = computed(() =>
  provenanceBarOption(data.value?.bounces_by_provider || [], PROVIDER_COLORS)
)

const leadiqStatusChartOption = computed(() =>
  provenanceBarOption(data.value?.bounces_by_leadiq_status || [], LEADIQ_COLORS)
)

// ── Helpers ─────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString()
}

function pct(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`
}
</script>

<style scoped>
.deliverability {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* ── Filters ─────────────────────────────────────────────────────── */
.filterBar {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.filterPicker { width: 180px; }
.filterPickerWide { width: 240px; }
.filterInput {
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid var(--color-border, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #fff);
  color: var(--color-text-primary);
  outline: none;
}
.filterInput:focus { border-color: var(--color-accent, #059669); }
.filterSep { color: var(--color-text-tertiary); font-size: 13px; }

/* ── Status ──────────────────────────────────────────────────────── */
.statusMsg {
  padding: var(--space-5);
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
}
.statusMsg.error { color: var(--color-danger, #dc2626); }

/* ── Summary row ─────────────────────────────────────────────────── */
.summaryRow {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.statCard {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: var(--space-4) var(--space-5);
  min-width: 150px;
  flex: 1;
  border: 1px solid var(--color-border-subtle, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-surface-subtle, #faf9f7);
}

.statValue {
  font-size: 28px;
  font-weight: var(--fw-semibold);
  color: var(--color-accent, #059669);
  line-height: 1.2;
}
.statValueDanger {
  color: var(--color-danger, #dc2626);
}

.statSub {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.statLabel {
  font-size: 13px;
  font-weight: var(--fw-medium);
  color: var(--color-text-tertiary);
}

/* ── Charts ──────────────────────────────────────────────────────── */
.chartRow {
  display: flex;
  gap: var(--space-4);
}
.chartRow > .chartSection {
  flex: 1;
  min-width: 0;
}

.chartSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.chartTitle {
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  margin: 0;
}

.chartWrap {
  border: 1px solid var(--color-border-subtle, #e5e2dc);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #fff);
  padding: var(--space-3);
}

/* ── Bounce table ────────────────────────────────────────────────── */
.tableSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.tableWrap {
  overflow: visible;
}

/* ── Reply breakdown in stat card ─────────────────────────────────── */
.replyBreakdown {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  margin-top: 2px;
}
.replyPos { color: #10b981; font-weight: var(--fw-semibold); }
.replyNeg { color: #f59e0b; font-weight: var(--fw-semibold); }
.replyPending { color: var(--color-text-tertiary); }
.replySep { color: var(--color-text-tertiary); }

/* ── Detail tables (bounces + replies) ───────────────────────────── */
.detailTable {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-sm);
}

.detailTable th {
  text-align: left;
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  padding: var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
  white-space: nowrap;
}

.detailTable td {
  padding: var(--space-4) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle, #f0ede8);
  color: var(--color-text-primary);
}

.detailTable tbody tr:hover {
  background: var(--color-surface-subtle, #faf9f7);
}

.snippetCell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-secondary);
  font-size: 12px;
}

.outcomeBadge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--radius-full, 999px);
  font-size: 11px;
  font-weight: var(--fw-semibold);
}
.outcome-engaged {
  background: #d1fae5;
  color: #065f46;
}
.outcome-disqualified {
  background: #fef3c7;
  color: #92400e;
}
.outcome-pending {
  background: var(--color-surface-subtle, #f3f4f6);
  color: var(--color-text-tertiary);
}

.threadLink {
  font-size: 12px;
  color: var(--color-accent, #059669);
  text-decoration: none;
  white-space: nowrap;
}
.threadLink:hover {
  text-decoration: underline;
}

.reachoutPill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: var(--radius-full, 999px);
  background: var(--color-surface-subtle, #f3f4f6);
  border: 1px solid var(--color-border-subtle, #e5e2dc);
  font-size: 11px;
  font-weight: var(--fw-medium, 500);
  color: var(--color-text-primary);
  text-decoration: none;
  max-width: 220px;
  white-space: nowrap;
}
.reachoutPill:hover {
  background: var(--color-accent-subtle, #ecfdf5);
  border-color: var(--color-accent, #059669);
  color: var(--color-accent, #059669);
}
.reachoutPillLabel {
  overflow: hidden;
  text-overflow: ellipsis;
}

.textMuted { color: var(--color-text-tertiary); font-size: 12px; }

.emptyMsg {
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
  padding: var(--space-7) 0;
}
</style>
